import {
  AbTestingChemistryMonitorIcon,
  CheckShieldIcon,
  LoginIcon,
  TranslateIcon,
} from "@bookingcom/bui-assets-react/streamline";
import { Store } from "@tanstack/react-store";
import { VALID_USER_ROLES, type LocalStorageConfigItem } from "../types";
import { getEnvironment } from "../utils/environment";
import { getObjectKeys } from "../object";

export const localStorageConfigItems = [
  {
    icon: AbTestingChemistryMonitorIcon,
    title: "Mock API Responses",
    description: "Enable/disable API mocking with MSW",
    type: "boolean",
    localStorageKey: "psmConfigEnableMSW",
    reloadOnChange: true,
  },
  {
    icon: CheckShieldIcon,
    title: "Override User Role",
    description: "Override the Auth0 user role",
    type: "string",
    possibleValues: VALID_USER_ROLES,
    localStorageKey: "psmConfigOverrideUserRole",
    reloadOnChange: true,
  },
  {
    icon: LoginIcon,
    title: "First Login",
    description: "Consider as first login and show onboarding tour",
    type: "boolean",
    localStorageKey: "psmConfigFirstLogin",
    reloadOnChange: false,
  },
  {
    icon: TranslateIcon,
    title: "Show Copy Tags",
    description: "Display copy tags instead of translations",
    type: "boolean",
    localStorageKey: "psmConfigShowCopyTags",
    reloadOnChange: false,
  },
] as const satisfies readonly LocalStorageConfigItem[];

type ConfigItem = (typeof localStorageConfigItems)[number];
export type LocalStorageConfigState = {
  [K in ConfigItem as K["localStorageKey"]]?: K["type"] extends "boolean"
    ? boolean
    : K["type"] extends "string"
      ? string
      : never;
};

const getDefaultLocalStorageConfigState = (): LocalStorageConfigState => ({});

const parseBooleanConfigItem = (value: string | null): boolean | undefined => {
  if (!value) return;

  return value.toLowerCase() === "true" || value === "1";
};

const parseStringConfigItem = (value: string | null, possibleValues: string[]): string | undefined => {
  if (!value) return;
  if (!possibleValues.length) return value;

  return possibleValues.includes(value) ? value : undefined;
};

function loadInitialState(): LocalStorageConfigState {
  const initialState: LocalStorageConfigState = getDefaultLocalStorageConfigState();
  const { internal } = getEnvironment();

  if (internal) {
    localStorageConfigItems.forEach((item) => {
      const value = localStorage.getItem(item.localStorageKey);

      if (item.type === "boolean") {
        const parsedValue = parseBooleanConfigItem(value);
        if (parsedValue) initialState[item.localStorageKey] = parsedValue;
      } else if (item.type === "string") {
        const parsedValue = parseStringConfigItem(value, [...item.possibleValues]);
        if (parsedValue) initialState[item.localStorageKey] = parsedValue;
      }
    });
  }

  return initialState;
}

export const localStorageConfigStore = new Store<LocalStorageConfigState>(loadInitialState());

export const setLocalStorageConfigByKey = <K extends keyof LocalStorageConfigState>(
  localStorageKey: K,
  value: LocalStorageConfigState[K]
) => {
  const configItem = localStorageConfigItems.find((item) => item.localStorageKey === localStorageKey);
  if (!configItem) return;

  // Remove from localStorage if value is empty
  if (!value || value === "") {
    localStorage.removeItem(configItem.localStorageKey);
  } else {
    localStorage.setItem(configItem.localStorageKey, String(value));
  }
  localStorageConfigStore.setState((state) => ({
    ...state,
    [localStorageKey]: value,
  }));
};

export const setLocalStorageConfig = (localStorageConfigState: LocalStorageConfigState) => {
  getObjectKeys(localStorageConfigState).forEach((key) => {
    const value = localStorageConfigState[key];

    // Remove from localStorage if value is empty
    if (!value || value === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  });
  localStorageConfigStore.setState((prevState) => ({ ...prevState, ...localStorageConfigState }));
};
